import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Lock } from "lucide-react";
import { useToast } from "../../components/ui/toast";
import { ApiV2Error } from "../../lib/api-v2";
import { cn } from "../../lib/utils";
import { OrderTotalsBreakdown } from "../orders/order-totals-breakdown";
import { usePayForCart, useQuote } from "../orders/queries";
import { credits, rupees } from "../products/types";
import type { CartSummary } from "./types";

interface CheckoutSummaryProps {
  cart: CartSummary;
  /** Unlocks the cart so it can be changed again. */
  onBackToCart: () => void;
  reopening: boolean;
}

const errorMessage = (err: unknown, fallback: string) =>
  err instanceof ApiV2Error || err instanceof Error ? err.message : fallback;

/**
 * The cart locked at checkout: what the account is about to pay for, with GST, and
 * the way to pay. A successful payment opens the order.
 */
export function CheckoutSummary({ cart, onBackToCart, reopening }: CheckoutSummaryProps) {
  const quote = useQuote(true);
  const pay = usePayForCart();
  const navigate = useNavigate();
  const toast = useToast();

  const proceed = () =>
    pay.mutate(undefined, {
      onSuccess: (order) => navigate(`/orders/${order.id}`),
      onError: (err) => toast.danger("Payment not completed", errorMessage(err, "Try again.")),
    });

  const busy = pay.isPending || reopening;

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-8">
      <div className="flex items-start gap-3">
        <CheckCircle2 className="mt-1 h-6 w-6 shrink-0 text-success" />
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-ink">Checkout complete</h1>
          <p className="mt-1 text-sm text-ink-2">Your cart is locked at these items.</p>
        </div>
      </div>

      <section className="rounded-2xl border border-line bg-elevated shadow-sm" aria-label="Checkout summary">
        <ul className="divide-y divide-line">
          {cart.items.map((item) => (
            <li key={item.product_id} className="flex items-center justify-between gap-4 px-6 py-4 text-sm">
              <div>
                <p className="font-semibold text-ink">{item.name}</p>
                {item.rate_per_minute !== null ? (
                  <p className="tabular-nums text-ink-3">
                    {rupees(item.rate_per_minute)} / min · {credits(item.credits)} credits
                  </p>
                ) : (
                  <p className="tabular-nums text-ink-3">{item.quantity} × {rupees(item.unit_price)}</p>
                )}
              </div>
              <span className="font-semibold tabular-nums text-ink">{rupees(item.total_price)}</span>
            </li>
          ))}
        </ul>
        {cart.credits > 0 && (
          <div className="flex items-center justify-between border-t border-line bg-brand-soft/40 px-6 py-3 text-sm">
            <span className="font-medium text-ink">Credits you get (minutes of calling)</span>
            <span className="font-bold tabular-nums text-ink">{credits(cart.credits)}</span>
          </div>
        )}
        <div className="border-t border-line px-6 py-4">
          {quote.data ? (
            <OrderTotalsBreakdown totals={quote.data} totalLabel="Total payable" />
          ) : quote.isError ? (
            <p className="text-sm text-danger">{errorMessage(quote.error, "Could not work out the total.")}</p>
          ) : (
            <div className="h-20 animate-pulse rounded-lg bg-soft" />
          )}
        </div>
      </section>

      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
        <button
          onClick={onBackToCart}
          disabled={busy}
          className="inline-flex h-11 items-center gap-2 rounded-lg border border-line bg-elevated px-5 text-sm font-semibold text-ink hover:bg-soft disabled:cursor-wait disabled:opacity-60"
        >
          <ArrowLeft className="h-4 w-4" /> {reopening ? "Opening cart…" : "Back to cart"}
        </button>
        <button
          onClick={proceed}
          disabled={busy || !quote.data}
          className={cn(
            "inline-flex h-11 items-center justify-center gap-2 rounded-lg px-5 text-sm font-semibold text-white",
            busy || !quote.data ? "cursor-not-allowed bg-line text-ink-3" : "bg-brand hover:bg-brand-hover",
          )}
        >
          <Lock className="h-4 w-4" />
          {pay.isPending ? "Processing payment…" : "Proceed to payment"}
        </button>
      </div>
    </div>
  );
}
